import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';

/**
 * §24.4 — the visible half of `ScopeGuard`.
 *
 * 37 routes carry `@RequireScope(...)`, but their list endpoints do not: a
 * branch-scoped manager holding `settings.update` is handed every branch, floor,
 * area and table in the tenant and only learns which are theirs from a 403 that
 * says `Out of scope for BRANCH <id>`. `useRequireScope()` decides that ahead of
 * the click; this renders the answer.
 *
 * It exists because `title` on a *disabled* `<button>` is not reliably shown —
 * Chrome does not hit-test disabled form controls, so the native tooltip never
 * fires and the control just greys out with no reason attached. Hanging the
 * reason on a wrapping span puts it on an element that still receives hover, so
 * a locked affordance always says why it is locked.
 *
 * The server stays authoritative either way: this only stops the client from
 * offering an action that is guaranteed to be refused.
 */
export function ScopeLock({
  locked,
  reason,
  children,
  className,
}: {
  locked: boolean;
  reason: string;
  children: ReactNode;
  className?: string;
}) {
  if (!locked) return <>{children}</>;
  return (
    <span title={reason} className={cn('inline-flex cursor-not-allowed', className)}>
      {children}
    </span>
  );
}

export default ScopeLock;
